import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Brush, CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import "./AllDriverBarsOverTime.css";
import { useGetDriversBreakdownMutation } from "../../../store/api/lumi/drivers";
import { debounce } from "lodash";
import { Spin } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import { getConceptFilter } from "../../../utils/getConceptFilter";
import { getType } from "../../../utils/date";
import { useGetConceptsFiltersMutation } from "../../../store/api/lumi/concepts";
import { formatDateToUtc } from "../../../utils/formatDate";
import { useSelector } from "react-redux";
import { getLineColor } from "../../../utils/getLineColor";

export function DriversOverTime(props) {
    const [chartData, setChartData] = useState([]);
    const [getDriversBreakdown] = useGetDriversBreakdownMutation();
    const [getVolumes] = useGetConceptsFiltersMutation();
    const [loading, setLoading] = useState(true);
    const [conceptColors, setConceptColors] = useState({});

    const labelFontSize = useSelector((state) => {
        const fontSizes = state.fontManager.fontSizes;
        const normalizedKey = Object.keys(fontSizes).find(
            (key) => key.trim() === "labelFontSize",
        );
        return fontSizes[normalizedKey]  || 16;
    });

    const {
        id,
        selectedFieldDriverName,
        filters,
        seletctedType,
        selectedDateName,
        interval,
        smoothLine
    } = props;

    const conceptFilter = useMemo(
        () => JSON.stringify(getConceptFilter(props)),
        [props]
    );

    const getConcepts = useCallback(async () => {
        const conceptFilter = getConceptFilter(props);
        const { data: dataConcepts } = await getVolumes({
            id: props.id,
            concept_selector: conceptFilter,
            filter: filters,
        });
        props.setFilterDocCount(dataConcepts.filter_count);
    }, [filters]);

    const getDrivers = useCallback(async () => {
        setLoading(true);
        const { data } = await getDriversBreakdown({
            id,
            concept_selector: JSON.parse(conceptFilter),
            score_field: selectedFieldDriverName,
            filter: filters,
            match_type: getType(seletctedType),
            breakdowns: [{ name: selectedDateName, interval: interval }],
        });

        const breakdownData = data[0].buckets;
        const conceptsMap = {};
        const conceptColors = {};

        breakdownData.forEach(({ label, drivers }) => {
            const formattedDate = formatDateToUtc(label, props.interval);
            drivers.forEach(({ name, average_score, color }) => {
                if (!conceptsMap[name]) {
                    conceptsMap[name] = [];
                    if (color) conceptColors[name] = color;
                }

                let count = average_score;

                conceptsMap[name].push({
                    date: formattedDate,
                    [name]: count,
                });
            });
        });
        setConceptColors(conceptColors);
        const aggregatedData = Object.values(conceptsMap)
            .flat()
            .reduce((acc, curr) => {
                if (acc.has(curr.date)) {
                    const existingEntry = acc.get(curr.date);
                    const updatedEntry = { ...existingEntry };
                    for (const key in curr) {
                        if (key !== "date") {
                            updatedEntry[key] =
                                (updatedEntry[key] || 0) + curr[key];
                        }
                    }
                    const sumOfProperties = Object.keys(updatedEntry)
                        .filter((key) => key !== "date")
                        .reduce((sum, key) => sum + updatedEntry[key], 0);
                    if (sumOfProperties > 0) {
                        acc.set(curr.date, updatedEntry);
                    }
                } else {
                    const sumOfProperties = Object.keys(curr)
                        .filter((key) => key !== "date")
                        .reduce((sum, key) => sum + curr[key], 0);

                    if (sumOfProperties > 0) {
                        acc.set(curr.date, curr);
                    }
                }
                return acc;
            }, new Map())
            .values();

        const aggregatedDataArray = Array.from(aggregatedData);
        setChartData(aggregatedDataArray);
        setLoading(false);
    }, [
        filters,
        getDriversBreakdown,
        id,
        interval,
        selectedDateName,
        selectedFieldDriverName,
        seletctedType,
        conceptFilter,
    ]);

    useEffect(() => {
        setLoading(true);
        const debouncedGetDrivers = debounce(getDrivers, 1000);

        debouncedGetDrivers();

        return () => {
            debouncedGetDrivers.cancel();
        };
    }, [getDrivers]);

    useEffect(() => {
        setLoading(true);
        const debouncedGetConcepts = debounce(getConcepts, 1000);

        debouncedGetConcepts();

        return () => {
            debouncedGetConcepts.cancel();
        };
    }, [getConcepts]);

    if (loading) {
        return (
            <Spin
                className={"spinner-by-center"}
                indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
            />
        );
    }

    const valueFormatter = (value) => {
        return `${Number(value).toFixed(2)}`;
    };

    const allValues = chartData.flatMap((item) =>
        Object.values(item).filter((value) => typeof value === "number")
    );
    const minValue = Math.min(...allValues) ;
    const maxValue = Math.max(...allValues) ;
    return (
        <div className="volume-compare"  style={{height: props.widget.layout.h * 39 - 150}}
             onMouseDown={(e) => {
                 if (e.target.closest(".recharts-brush")) {
                     e.stopPropagation();
                     e.preventDefault()
                 }
             }}
        >
            <ResponsiveContainer width="100%" height="100%">
                <LineChart
                    data={chartData}
                    margin={{ top: 5, right: 20, left: 10, bottom: 5 }}
                >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date"  tick={{ fontSize: 16, color: "#000", fontWeight: "bold" }}/>
                    <YAxis
                        label={{
                            value: "Average score",
                            angle: -90,
                            position: "insideLeft",
                            offset: 0,
                            style: { textAnchor: "middle", fontSize: 16, color: "#000", fontWeight: "bold" },
                        }}
                        tickFormatter={(tick) =>
                            new Intl.NumberFormat("en", {
                                notation: "compact",
                            }).format(tick)
                        }
                        tick={{ fontSize: 16, color: "#000", fontWeight: "bold", }}
                        domain={[minValue, maxValue]}
                    />
                    <Tooltip formatter={valueFormatter} />
                    <Legend
                        wrapperStyle={{
                            fontSize: `${labelFontSize}px`,
                            fontWeight: "bold",
                            color: "#000",
                        }}
                    />
                    <Brush
                        dataKey="date"
                        height={30}
                        stroke="#696969"
                        travellerWidth={12}
                        gap={2}
                        startIndex={0}
                        endIndex={chartData.length - 1}
                        fill="#D3D3D3"
                        travellerStyle={{
                            stroke: "#696969",
                            fill: "#A9A9A9",
                            cursor: "pointer",
                        }}
                        selectedBoxStyle={{
                            stroke: "#A9A9A9",
                            fill: "rgba(169, 169, 169, 0.2)",
                            cursor: "move",
                        }}
                        padding={{ top: 2, bottom: 2 }}
                    />
                    {
                        Object.keys(chartData[0] ?? {})
                            .filter((key) => key !== "date")
                            .map((key, idx) => (
                                <Line
                                    strokeWidth={2}
                                    dot={!smoothLine}
                                    type={smoothLine ? "monotone" : "linear"}
                                    dataKey={key}
                                    stroke={conceptColors[key] || getLineColor(idx)}
                                    key={key}
                                />
                            ))
                    }
                </LineChart>
            </ResponsiveContainer>
        </div>
    );
}
